import { SEQUENTIAL_VAR, STATUS_VAR } from "./palette"

/**
 * A small radar for the framework-emphasis profile.
 *
 * Hand-rolled rather than reached for from ECharts, which the analytics module
 * uses: this demo's charts are all plain HTML/SVG against one validated palette,
 * and a single ECharts canvas dropped into the middle of them would arrive with
 * its own type scale, its own tooltip and its own colours. Eighty lines is a
 * cheaper price than that inconsistency.
 *
 * The viewBox is SQUARE and `preserveAspectRatio` is left at its default. That
 * is not incidental — setting it to `none` on a non-square box scales x and y
 * independently, which smears every label and turns circles into ellipses. It
 * cost this demo a rebuilt chart once already.
 *
 * A radar is a weak choice for comparing magnitudes and a good one for showing
 * SHAPE, which is exactly the question here: not "how much evidence rigour" but
 * "what does this combination emphasise, and what does it ignore".
 */

export interface RadarAxis {
    id: string
    label: string
    value: number
}

interface RadarChartProps {
    axes: RadarAxis[]
    max?: number
    size?: number
    /** Rendered label size in CSS px, held constant across `size`. */
    labelPx?: number
    /**
     * Off for the 44px per-row glyphs, where there is no room for text and the
     * shape alone is the signal. It also swaps the viewBox: the wide box below
     * exists purely as a label gutter, and keeping it on a glyph would shrink
     * the plot to a third of the tile.
     */
    labels?: boolean
    /**
     * Individual profiles drawn as dashed outlines on top of the filled `axes`
     * polygon. `frameworkProfile` combines by `Math.max`, not by sum — which is
     * correct, emphasis is not additive — but it means adding a second framework
     * whose profile is subsumed leaves the filled shape IDENTICAL, so the chart
     * silently claims nothing happened. The outlines show the members.
     */
    series?: Array<{ id: string; values: number[] }>
}

const VIEWBOX_WIDTH = 168

const GLYPH_GRID = "color-mix(in srgb, currentColor 30%, transparent)"

const CENTRE = 50
const RADIUS = 32

function point(index: number, count: number, radius: number): [number, number] {
    // Start at twelve o'clock and go clockwise, which is how people read a dial.
    const angle = (Math.PI * 2 * index) / count - Math.PI / 2
    return [CENTRE + Math.cos(angle) * radius, CENTRE + Math.sin(angle) * radius]
}

export default function RadarChart({
    axes,
    max = 4,
    size = 260,
    labels = true,
    labelPx = 11,
    series,
}: RadarChartProps) {
    if (axes.length < 3) return null
    const count = axes.length

    // The viewBox is 168 units wide and renders at `size` px, so a fixed
    // `fontSize` scales with the chart — a 220px radar drew ~6.5px labels.
    // Solve for the rendered size instead and the text stays legible at any
    // dimension.
    const labelFontSize = (labelPx * VIEWBOX_WIDTH) / size

    const rings = (labels ? [0.25, 0.5, 0.75, 1] : [1]).map((fraction) =>
        axes
            .map((_, index) => point(index, count, RADIUS * fraction).join(","))
            .join(" "),
    )

    const shape = axes
        .map((axis, index) =>
            point(index, count, (RADIUS * Math.max(0, Math.min(max, axis.value))) / max).join(","),
        )
        .join(" ")

    const empty = axes.every((axis) => axis.value === 0)

    return (
        <svg
            // Wider than the plot, because the LABELS are the widest thing here:
            // "Modalitätsvielfalt" anchored at the right vertex runs ~36 units
            // past it, and a 0 0 100 100 box simply clips them. The box stays
            // proportional and `preserveAspectRatio` stays at its default, so
            // nothing is scaled unevenly.
            viewBox={labels ? "-34 -8 168 116" : "15 15 70 70"}
            width={size}
            height={labels ? (size * 116) / 168 : size}
            role="img"
            aria-label={axes.map((axis) => `${axis.label}: ${axis.value} von ${max}`).join(", ")}
            className="max-w-full"
        >
            {rings.map((points, index) => (
                <polygon
                    key={index}
                    points={points}
                    fill="none"
                    // The shared grid token is currentColor at 14% — right
                    // behind a 260px chart, invisible behind a 44px glyph, where
                    // it left the shape reading as a floating shard rather than
                    // a profile inside a frame.
                    stroke={labels ? STATUS_VAR.grid : GLYPH_GRID}
                    strokeWidth={labels ? 0.4 : 1.2}
                />
            ))}
            {labels && axes.map((axis, index) => {
                const [x, y] = point(index, count, RADIUS)
                return (
                    <line
                        key={axis.id}
                        x1={CENTRE}
                        y1={CENTRE}
                        x2={x}
                        y2={y}
                        stroke={STATUS_VAR.grid}
                        strokeWidth={0.4}
                    />
                )
            })}

            {!empty && (
                <polygon
                    points={shape}
                    fill={SEQUENTIAL_VAR}
                    fillOpacity={0.2}
                    stroke={SEQUENTIAL_VAR}
                    strokeWidth={1.2}
                    strokeLinejoin="round"
                />
            )}
            {series && series.length > 1 &&
                series.map((entry) => (
                    <polygon
                        key={entry.id}
                        points={entry.values
                            .map((value, index) =>
                                point(index, count, (RADIUS * Math.max(0, Math.min(max, value))) / max).join(","),
                            )
                            .join(" ")}
                        fill="none"
                        stroke={SEQUENTIAL_VAR}
                        strokeWidth={0.8}
                        strokeDasharray="3 2"
                        strokeOpacity={0.55}
                        strokeLinejoin="round"
                    />
                ))}
            {!empty &&
                axes.map((axis, index) => {
                    const [x, y] = point(
                        index,
                        count,
                        (RADIUS * Math.max(0, Math.min(max, axis.value))) / max,
                    )
                    return <circle key={axis.id} cx={x} cy={y} r={1.3} fill={SEQUENTIAL_VAR} />
                })}

            {labels && axes.map((axis, index) => {
                const [x, y] = point(index, count, RADIUS + 11)
                // Nudge the anchor by position so labels never overlap the shape.
                const anchor = x < CENTRE - 2 ? "end" : x > CENTRE + 2 ? "start" : "middle"
                return (
                    <text
                        key={axis.id}
                        x={x}
                        y={y}
                        textAnchor={anchor}
                        dominantBaseline="middle"
                        fontSize={labelFontSize}
                        fill="currentColor"
                        opacity={0.85}
                    >
                        {axis.label}
                    </text>
                )
            })}
        </svg>
    )
}
